import React, {useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import * as constants from "../../constants/authConstants";
import * as routeConstants from "../../constants/routeConstants";

import { login, logout, refreshToken } from "../../store/auth/auth-actions";
import { getMyProfile } from "../../store/auth/auth-actions";

import {getAbortSignal} from '../../util/signalHelper';

import {notificationActions} from "../../store/notification/notification-slice";
import AuthenticatingGenesysDesktop from "./AuthenticatingGenesysDesktop";

import MediatorPolling from "./MediatorPolling";
import AuthenticationError from "./AuthenticationError";

import platformClient from "purecloud-platform-client-v2";

import PopupPolling from "./PopupPolling";


//initialize Chromium so there can be communication between parent and popup?
document.cookie = "dummy=initializechromium; Secure; SameSite=None";

const checkContainsBrand = (brandname) => {
  if (!navigator) return false;
  if (!navigator.userAgentData) return false;
  if (navigator.userAgentData.brands) return false;
  return !!navigator.userAgentData.brands.find(brand => brand.brand === brandname);
}

const checkIsEdge = () => {
  return checkContainsBrand("Microsoft Edge");
}

const checkIsGoogleChrome = () => {
  return checkContainsBrand("Google Chrome");
}

const checkIsGenesysDesktop = () => {
  return checkContainsBrand("Chromium") && !(checkIsEdge() || checkIsGoogleChrome());
}

const getSearchParameter = (parameter) => {
  if (!containsSearchParameter(parameter)) return false;
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(parameter);
}

const containsSearchParameter = (parameter) => {
  if (!parameter) return false;
  return window.location.search.includes(parameter);
}

const containsUrlParameter = (parameter) => {
  if (!parameter) return false;
  return window.location.href.includes(parameter);
}

const getUrlParameter = (parameter) => {
  if (!containsUrlParameter(parameter)) return null;
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(parameter);
}

const STATE_IDLE = -1;

const STATE_DETERMINING_STATUS = 0;
const STATE_LOGGED_OUT = 1;
const STATE_LOGGING_OUT = 2;
const STATE_EXPIRED = 3;
const STATE_INVALID_TOKEN = 4;
const STATE_LOGGING_IN_VERIFIER = 5
const STATE_LOGGING_IN_CHALLENGE = 6
const STATE_LOGGING_IN_AWAITING_CHALLENGE = 7
const STATE_LOGGING_IN_TOKEN_REQUEST = 8

const STATE_LOGGED_IN = 10
const STATE_LOGGING_OUT_2 = 12;
const STATE_LOGGING_IN_CODE_REQUEST = 21;
const STATE_LOGGING_IN_CODE_RESPONSE = 22;
const STATE_LOGGING_IN_CODE_ERROR_RESPONSE = 23;
const STATE_LOGGED_IN_REDIRECTING = 24;
const STATE_VERIFY_TOKEN = 25;
const STATE_REFRESH_TOKEN = 26;

const STATE_LOGGED_IN_REDIRECT = 27;
const STATE_LOGGED_IN_POPUP_ERROR_NOTIFICATION = 28;

const LOGGING_IN_CODE_REQUEST_IFRAME = 29;
const STATE_IFRAME_ERROR = 33;
const STATE_IFRAME_SUCCESS = 34;

const STATE_MEDIATOR_INIT = 35;
const STATE_MEDIATOR_POLLING = 36;
const STATE_MEDIATOR_FAILURE = 37;

const STATE_LOGIN_GENESYS_SDK_ENABLED = 38;

const STATE_POPUP_FAILED = 41;

const STATE_LOGGING_IN_TOKEN_REQUEST_GENESYS_CLIENT = 32;

const STATE_LOGGED_IN_REFRESH_PROFILE = 42;
const STATE_LOGGED_IN_AWAIT_PROFILE = 43;

const STATE_LOGGED_IN_REFRESH_PROFILE_NO_REDIRECT = 44;
const STATE_LOGGED_IN_AWAIT_PROFILE_NO_REDIRECT = 45;

const VALID_IS_REFRESHING_FLAG_STATES = [
  STATE_DETERMINING_STATUS, STATE_LOGGING_IN_VERIFIER, STATE_LOGGING_IN_CHALLENGE, STATE_LOGGING_IN_TOKEN_REQUEST,
  STATE_LOGGING_IN_CODE_REQUEST, STATE_LOGGING_IN_CODE_RESPONSE, STATE_MEDIATOR_INIT, STATE_MEDIATOR_POLLING, 
  STATE_LOGIN_GENESYS_SDK_ENABLED, STATE_REFRESH_TOKEN, STATE_LOGGING_IN_AWAITING_CHALLENGE, STATE_LOGGED_IN_REDIRECTING, 
  STATE_LOGGED_IN, STATE_EXPIRED, STATE_LOGGED_IN_AWAIT_PROFILE_NO_REDIRECT, STATE_LOGGED_IN_REFRESH_PROFILE_NO_REDIRECT
]

const AUTH_POPUP_WINDOW_NAME = "AUTH_POPUP";
//const TOKEN_REFRESH_INTERVAL_IN_MILLIS = process.env.REACT_APP_OAUTH_TOKEN_REFRESH_INTERVAL_IN_MILLIS;// ? process.env.REACT_APP_OAUTH_TOKEN_REFRESH_INTERVAL_IN_MILLIS : 10*60*60*1000;
const popupEnabled = constants.LOGIN_POPUP_ENABLED;
const isPopup = getUrlParameter(constants.URL_PARAM_POPUP) === "true" || window.name === AUTH_POPUP_WINDOW_NAME;
const isIframe = getUrlParameter(constants.URL_PARAM_IFRAME) === "true";
const useGenesysDesktopIframe = checkIsGenesysDesktop() && constants.LOGIN_ENABLE_GENESYS_DESKTOP_IFRAME;
const IS_DEV = process.env.REACT_APP_IS_DEV === 'true';

//if (!isPopup && checkIsGenesysDesktop() && IS_DEV) window.open('https://apps.mypurecloud.de', "ControlToClearAuth", "'width=600,height=400,resizable=yes,scrollbars=yes'");

const AuthenticationPKCE = props => {

  const isErrorResponse = containsUrlParameter(constants.URL_PARAM_ERROR);
  const isCodeResponse = containsUrlParameter(constants.URL_PARAM_CODE);

  const dispatch = useDispatch();
  const navigate = useNavigate(); 
  const location = useLocation();

  const {authResetter} = props; 

  const auth = useSelector((state) => state.auth);
  const {profile} = useSelector(state => state.auth);

  const isAuthenticating = location.pathname === routeConstants.ROUTE_AUTH;
  const isLogginOut = location.pathname === routeConstants.ROUTE_LOGOUT;
  
  const token = useMemo(() => auth.token, [auth.token]);
  const expiresAt = useMemo(() => auth.expiresAt ? new Date(auth.expiresAt) : null, [auth.expiresAt]);

  const codeChallenge = !isPopup ? getStoredCodeChallenge() : getUrlParameter(constants.URL_PARAM_CHALLENGE);

  const [pageAuthState, setPageAuthState] = useState({
    state: STATE_DETERMINING_STATUS,
    expired: false,
    isTokenRefresh: false,
    blockExpiredCheck: false,
    codeResponse: ""
  });

  const authState = useMemo(() => {
    return pageAuthState.state;
  }, [pageAuthState.state])

  const patchPageAuthState = useCallback((patchState) => {
    setPageAuthState((oldPageState) => {
      const newState = {...oldPageState, ...patchState}
      if (newState.isTokenRefresh && !VALID_IS_REFRESHING_FLAG_STATES.includes(newState.state)) {
        newState.isTokenRefresh = false;
      }
      return newState;
    })
  }, [setPageAuthState]);

  const setAuthState = useCallback((newState) => {
    patchPageAuthState({state: newState});
  }, [patchPageAuthState])

  const popupRedoIfClosed = useRef(false);
  const popupRef = useRef(null);
  const popupTimerRef = useRef(null);
  const mediatorTimerRef = useRef(null);

  console.log(`Authentication -> isAuthenticating=${isAuthenticating}, isLogginOut=${isLogginOut}, token set=${!!token}, popupEnabled=${popupEnabled}, isPopup=${isPopup}, windowName=${window.name}, isLoggedIn=${auth.isLoggedIn}, authState:${authState}`)

  const clearAuth = useCallback((logoutParams) => {
    clearCodeVerifier();
    clearCodeChallenge();
    clearCodeResponse();
    dispatch(logout(logoutParams));
    if (isPopup) return;
    if (isIframe) return;
    if (constants.LOGIN_GENESYS_SDK_ENABLED) return;
    navigate("/");
  }, [dispatch]);

  const redirectAfterLogin = useCallback(() => {
    if (isPopup) return;
    if (isIframe) return;
    const redirectToLocation = sessionStorage.getItem(constants.KEY_REDIRECT_TO_AFTER_LOGIN);

    if (!redirectToLocation || redirectToLocation === routeConstants.ROUTE_AUTH) {
      console.log(`Authentication -> useEffect: redirecting to home`);
      navigate("/");
      return;
    }

    sessionStorage.removeItem(constants.KEY_REDIRECT_TO_AFTER_LOGIN);
    console.log(`Authentication -> useEffect: redirecting to: ${redirectToLocation}`);
    navigate(redirectToLocation);
  }, []);

  const doLogout = useCallback((preferredState, logoutParams) => {
    setAuthState(!preferredState ? STATE_LOGGING_OUT_2 : preferredState);
    clearAuth(logoutParams);
  }, [setAuthState, clearAuth])

  const handleAuthError = useCallback((message, error, preferredState) => {
    doLogout(preferredState, {tokenRefreshFlag: false});
    if (isPopup) {
      window.opener.handlePopupAuthError(message, error);
      window.close();
      return;
    }
    if (isIframe) {
      window.parent.handleIframeAuthError(message, error);
      setAuthState(STATE_IFRAME_ERROR);
      return;
    }

    if (message) dispatch(notificationActions.setError(message));
    clearAuth({tokenRefreshFlag: false});
    if (constants.LOGIN_POPUP_ENABLED) setAuthState(STATE_POPUP_FAILED);
  }, [clearAuth, dispatch, setAuthState, doLogout]);

  useEffect(() => {
    if (!authResetter) return;
    if (constants.LOGIN_GENESYS_SDK_ENABLED) {
      clearAuth({tokenRefreshFlag: false});
      platformClient.ApiClient.instance.setAccessToken(null);
    }
    setAuthState(STATE_DETERMINING_STATUS);
  }, [authResetter , setAuthState, clearAuth]);

  //check expired
  useEffect(() => {
    const checkExpired = () => {
      if (authState !== STATE_LOGGED_IN) return;
      const calculatedIsExpired = expiresAt && (expiresAt.getTime() - new Date().getTime()) < 0;
      if (!calculatedIsExpired) return;

      setAuthState(STATE_EXPIRED);
    }

    const intervalId = setInterval(checkExpired, 5000);

    return () => clearInterval(intervalId);

  }, [authState, expiresAt, setAuthState])


  /*
    Sets the popup handler function on the main Window. 
    These are functions that get called by the popup on the main window in order to pass login information
  */

  useEffect(() => {
    console.log("Authentication -> useEffect handlers")

    const iframeCodeResponseHandler = (code) => {
      console.log("Authentication -> iframeCodeResponseHandler called")

      if (isIframe) return;
      if (isPopup) return;

      patchPageAuthState({
        codeResponse: code,
        state: STATE_LOGGING_IN_TOKEN_REQUEST_GENESYS_CLIENT
      });
    }

    const popupCodeResponseHandler = (code) => {
      console.log("Authentication -> popupCodeResponseHandler called")
      if (isIframe) return;
      if (isPopup) return;
      popupRedoIfClosed.current = false;
      patchPageAuthState({
        state: STATE_LOGGING_IN_TOKEN_REQUEST,
        codeResponse: code
      });
    }

    const popupAuthErrorHandler = (message, error) => {
      popupRedoIfClosed.current = false;
      console.error(`Authentication -> An error occurred during authentication using the popup - error: ${error} - message: ${message}`);
      if (message) dispatch(notificationActions.setError(message));
      setAuthState(STATE_LOGGED_IN_POPUP_ERROR_NOTIFICATION);
    }

    
    const iframeAuthErrorHandler = (message, error) => {
      popupRedoIfClosed.current = false;
      console.error(`Authentication -> An error occurred during authentication using the popup - error: ${error} - message: ${message}`);
      setAuthState(STATE_LOGGED_IN_POPUP_ERROR_NOTIFICATION);
      if (message) dispatch(notificationActions.setError(message));
    }

    window.handleIframeCodeResponse = iframeCodeResponseHandler;
    window.handlePopupCodeResponse = popupCodeResponseHandler;
    window.handlePopupAuthError = popupAuthErrorHandler;
    window.handleIframeAuthError = iframeAuthErrorHandler;
  }, [setAuthState, patchPageAuthState, dispatch]);


  /*
    The login process works as a state machine.
    It is important to leave a step in between when there is a navigation and a change of authState at the same time.
    This is to make sure that the state has been fully updated before proceeding.
    Please respect the way of working. The only state variable that should determine the next action is authState
    Not respecting this principle will break the authentication.

    Important variables that could influence individual steps are:
    - isAuthenticating: the page location is /auth
    - isLoggingOut: the page location is /logout
    - token: the token set in redux
    - expiresAt: the expiry date set in redux
    - isExpired: calculated based on expiresAt
    - isCodeResponse: the url contains a parameter indicating that there is a code response
    - isErrorResponse: the url contains a parameter indicating that there is a code error response
    - codeChallenge: the challenge generated during the login process
  */
  useEffect(() => {
    const handleState = () => {
      if (authState === STATE_LOGGED_IN) {
        
      } else if (authState === STATE_DETERMINING_STATUS) {
        determineStatus();
      } else if (authState === STATE_VERIFY_TOKEN) {
        handleVerifyToken();
      } else if (authState === STATE_EXPIRED || authState === STATE_INVALID_TOKEN) {
        handleExpired();
      } else if (authState === STATE_REFRESH_TOKEN) {
        if (constants.LOGIN_GENESYS_SDK_ENABLED) {
          setAuthState(STATE_LOGIN_GENESYS_SDK_ENABLED);
          return;
        }

        if (constants.LOGIN_MEDIATOR_ENABLED) {
          setAuthState(STATE_MEDIATOR_INIT);
          return;
        }
        patchPageAuthState({
          state:STATE_LOGGING_IN_VERIFIER,
          codeResponse: ""
        });
      } else if (authState === STATE_LOGGING_OUT) {
        handleLogout();
      } else if (authState === STATE_LOGGING_OUT_2) {
        if (!isAuthenticating) setAuthState(STATE_LOGGED_OUT);
      } else if (authState === STATE_LOGGING_IN_VERIFIER) {
        handleVerifier();
      } else if (authState === STATE_LOGGING_IN_CHALLENGE) {
        handleChallenge();
      } else if (authState === STATE_LOGGING_IN_AWAITING_CHALLENGE) {
        if (!!codeChallenge) {
          setAuthState(STATE_LOGGING_IN_CODE_REQUEST);
        }
      } else if (authState === STATE_LOGGING_IN_CODE_REQUEST) {
        handleCodeRequest();
      } else if (authState === STATE_LOGGING_IN_CODE_RESPONSE) {
        handleCodeResponse();
      } else if (authState === STATE_LOGGING_IN_TOKEN_REQUEST) {
        handleTokenRequest();
      } else if (authState === STATE_LOGGING_IN_TOKEN_REQUEST_GENESYS_CLIENT) {
        handleTokenRequestGenesysDesktop();
      } else if (authState === STATE_LOGGED_OUT) {
        if (!isAuthenticating) return;

        if (constants.LOGIN_GENESYS_SDK_ENABLED) {
          setAuthState(STATE_LOGIN_GENESYS_SDK_ENABLED);
          return;
        }

        if (constants.LOGIN_MEDIATOR_ENABLED) {
          setAuthState(STATE_MEDIATOR_INIT);
          return;
        }
        setAuthState(STATE_LOGGING_IN_VERIFIER);
      } else if (authState === STATE_LOGGED_IN_POPUP_ERROR_NOTIFICATION) {
        handleCodeErrorResponse(true)
      } else if (authState === STATE_LOGGING_IN_CODE_ERROR_RESPONSE) {
        handleCodeErrorResponse();
      } else if (authState === STATE_LOGGED_IN_REDIRECT) {
        redirectAfterLogin();
        setAuthState(STATE_LOGGED_IN_REDIRECTING);
      } else if (authState === STATE_LOGGED_IN_REDIRECTING) {
        if (!isAuthenticating) setAuthState(STATE_LOGGED_IN);
      } else if (authState === STATE_MEDIATOR_INIT) {
        handleInitializeMediator();
      } else if (authState === STATE_MEDIATOR_POLLING) {
        
      } else if (authState === STATE_MEDIATOR_FAILURE) {

      } else if (authState === STATE_LOGIN_GENESYS_SDK_ENABLED) {
        handleGenesysClient();
      } else if (authState === STATE_LOGGED_IN_REFRESH_PROFILE) {
        handleRefreshProfile();
        setAuthState(STATE_LOGGED_IN_AWAIT_PROFILE);
      } else if (authState === STATE_LOGGED_IN_AWAIT_PROFILE) {
        if (!token) {
          setAuthState(STATE_MEDIATOR_FAILURE);
          return;
        }
        if (profile) setAuthState(STATE_LOGGED_IN_REDIRECT);
      } else if (authState === STATE_LOGGED_IN_REFRESH_PROFILE_NO_REDIRECT) {
        handleRefreshProfile();
        setAuthState(STATE_LOGGED_IN_AWAIT_PROFILE_NO_REDIRECT);
      } else if (authState === STATE_LOGGED_IN_AWAIT_PROFILE_NO_REDIRECT) {
        if (profile) setAuthState(STATE_LOGGED_IN);
      }
    }

    const determineStatus = () => {
      if (isLogginOut) {
        setAuthState(STATE_LOGGING_OUT);
        return;
      }

      if (isCodeResponse) {
        if (constants.LOGIN_GENESYS_SDK_ENABLED) {
          setAuthState(STATE_LOGIN_GENESYS_SDK_ENABLED)
          return;
        }
        setAuthState(STATE_LOGGING_IN_CODE_RESPONSE);
        return;
      }

      if (isErrorResponse) {
        setAuthState(STATE_LOGGING_IN_CODE_ERROR_RESPONSE);
        return;
      }

      if (!!token && !isPopup && !isIframe) {
        if (!!expiresAt) {
          const isExpired = expiresAt && (expiresAt.getTime() - new Date().getTime()) < 0;
          if (isExpired) {
            setPageAuthState({state: STATE_EXPIRED, isTokenRefresh: true});
            return;
          }
        }

        if (!auth.isLoggedIn) {
          setAuthState(STATE_VERIFY_TOKEN);
          return;
        }

        if (isAuthenticating) {
          redirectAfterLogin();
          setAuthState(STATE_LOGGED_IN_REDIRECTING);
          return;
        }
        setAuthState(STATE_LOGGED_IN_REFRESH_PROFILE);
        return;
      }

      if (isAuthenticating) {
        if (constants.LOGIN_GENESYS_SDK_ENABLED) {
          setAuthState(STATE_LOGIN_GENESYS_SDK_ENABLED);
          return;
        }

        if (constants.LOGIN_MEDIATOR_ENABLED) {
          setAuthState(STATE_MEDIATOR_INIT);
          return;
        }
        setAuthState(STATE_LOGGING_IN_VERIFIER);
        return;
      }

      setAuthState(STATE_LOGGED_OUT);
    }

    const handleVerifier = () => {
      const codeVerifier = generateCodeVerifier();
      storeCodeVerifier(codeVerifier);
      clearCodeChallenge();
      clearCodeResponse();
      setAuthState(STATE_LOGGING_IN_CHALLENGE);
    }

    const handleChallenge = async () => {
      const challenge = await calculateCodeChallenge(getStoredCodeVerifier());
      storeCodeChallenge(challenge);
      setAuthState(STATE_LOGGING_IN_AWAITING_CHALLENGE);
    }

    const handleCodeRequest = () => {
      const loginUrl = generatePKCERedirectCodeUrl(codeChallenge);

      if (useGenesysDesktopIframe) {
        setAuthState(LOGGING_IN_CODE_REQUEST_IFRAME);
        return;
      }

      if (popupEnabled && !isPopup) {
        console.log("Authentication -> handleCodeRequest -> will open login popup");
        popupRedoIfClosed.current=true; //set before opening popup
        popupRef.current = window.open(`${loginUrl}`, AUTH_POPUP_WINDOW_NAME, "'width=600,height=400,resizable=yes,scrollbars=yes'");
        monitorPopup();
      } else {
        console.log("Authentication -> handleCodeRequest -> will direct to login url");
        window.location.href = loginUrl;
      }
    }

    const handleCodeResponse = () => {
      console.log("Authentication -> handleCodeResponse")

      if (isErrorResponse) {
        setAuthState(STATE_LOGGING_IN_CODE_ERROR_RESPONSE);
        return;
      }

      if (!isCodeResponse) return;

      //get token from url
      const code = getUrlParameter(constants.URL_PARAM_CODE);
      
      if (isIframe) {
        window.parent.handleIframeCodeResponse(code);
        setAuthState(STATE_IFRAME_SUCCESS);
      }

      if (isPopup) {
        window.opener.handlePopupCodeResponse(code);
        window.close();
        return;
      }

      setPageAuthState({
        state: STATE_LOGGING_IN_TOKEN_REQUEST,
        codeResponse: code
      });
    }

    const handleCodeErrorResponse = (noMessageIfNoParameter = false) => {
      const reason = getUrlParameter(constants.URL_PARAM_ERROR);
      let errorMessage = null;
      if (reason) {
        if (reason === constants.ERROR_VALUE_UNAUTHORIZED_CLIENT) {
          errorMessage = "Authentication failed because the client is unauthorized. Please contact your system administrator";
        } else if (!noMessageIfNoParameter) {
          errorMessage = "Authentication failed";
        }
      }

      handleAuthError(errorMessage);
    }

    const handleTokenRequest = async () => {
      if (useGenesysDesktopIframe) return;
      const params = {
        grant_type: "authorization_code",
        code: pageAuthState.codeResponse,
        redirect_uri: popupEnabled ? constants.REDIRECT_URI_POPUP : constants.REDIRECT_URI, //won't redirect, but is just as a control measure, needs to match the redirect uri the code was requested with
        client_id: constants.CLIENT_ID,
        code_verifier: getStoredCodeVerifier(),
        code_challenge_method: constants.PKCE_CHALLENGE_MODE,
        environment: constants.AUTH_ENVIRONMENT
      };

      let response = null;

      try {
        response = await fetch(constants.LOGIN_TOKEN_POPUP_PROXY_URL, {
          method: "POST",
          headers: {
            "Content-Type": constants.LOGIN_USE_AWS_LAMBDA_FOR_TOKEN_REQUEST_ENABLED ? 
            "application/json" :
            "application/x-www-form-urlencoded"
          },
          body: 
              constants.LOGIN_USE_AWS_LAMBDA_FOR_TOKEN_REQUEST_ENABLED ? 
              JSON.stringify(params) : 
              (new URLSearchParams(params)).toString()
        });

        if (response.status !== 200) {
          const errorResponse = await response.text();
          console.error(`Authentication -> Fetching authentication token failed -> ${errorResponse}`);
          handleAuthError("Could not fetch authentication information. Please try again or contact your administrator", `Authentication -> Fetching authentication token failed -> ${errorResponse}`, STATE_POPUP_FAILED);
          return;
        }

        const responseBody = JSON.parse(await response.text());
        const {access_token, expires_in} = responseBody;
        const expiresAt = new Date(new Date().getTime() + (1000 * expires_in));
        
        setAuthState(pageAuthState.isTokenRefresh 
          ? STATE_LOGGED_IN_REFRESH_PROFILE_NO_REDIRECT 
          : STATE_LOGGED_IN_REFRESH_PROFILE,
        );

        dispatch(login(access_token, expiresAt, pageAuthState.isTokenRefresh));
      } catch (e) {
        console.error(`Authentication -> An error occurred during the authentication process ${e.message}`);
        console.error(e);
        handleAuthError("An error occurred during the authentication process, please contact your administrator", e.message, STATE_POPUP_FAILED);
      }
    }

    const handleTokenRequestGenesysDesktop = async () => {
      if (!useGenesysDesktopIframe) return;
      const params = {
        grant_type: "authorization_code",
        code: pageAuthState.codeResponse,
        redirect_uri: constants.REDIRECT_URI_IFRAME,
        client_id: constants.CLIENT_ID,
        code_verifier: getStoredCodeVerifier(),
        code_challenge_method: constants.PKCE_CHALLENGE_MODE,
        environment: constants.AUTH_ENVIRONMENT
      };

      let response = null;

      try {
        response = await fetch(constants.LOGIN_TOKEN_GENESYS_DESKTOP_PROXY_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(params)
        });


        if (response.status !== 200) {
          const errorResponse = await response.text();
          console.error(`Authentication -> Fetching authentication token failed -> ${errorResponse}`);
          handleAuthError("Could not fetch authentication information. Please try again or contact your administrator", `Authentication -> Fetching authentication token failed -> ${errorResponse}`, STATE_POPUP_FAILED);
          return;
        }

        const responseBody = JSON.parse(await response.text());
        const {access_token, expires_in} = responseBody;
        const expiresAt = new Date(new Date().getTime() + (1000 * expires_in));

        setAuthState(STATE_LOGGED_IN_REDIRECTING);
        dispatch(login(access_token, expiresAt, pageAuthState.isTokenRefresh));
        redirectAfterLogin(); 
      } catch (e) {
        console.error(`Authentication -> An error occurred during the authentication process ${e.message}`);
        console.error(e);
        handleAuthError("An error occurred during the authentication process, please contact your administrator", e.message, STATE_POPUP_FAILED);
      }
    }
      
    const monitorMediator = () => {
      mediatorTimerRef.current = setInterval(async () => {
      clearInterval(mediatorTimerRef.current);

      let response = null;

      try {
        response = await fetch(constants.LOGIN_MEDIATOR_TOKEN_URL,    
          {       
            method: "GET",
            headers: {
              "Content-Type": "application/json"
            }}
        );

        const bodyText = await response.text();

        if (!bodyText) {
          setAuthState(STATE_MEDIATOR_FAILURE);
          return;
        }

        const body = await JSON.parse(bodyText);
        
        if (body.inProgress) {
          monitorMediator();
          return;
        }
        
        if (body.error) {
          setAuthState(STATE_MEDIATOR_FAILURE);
          return;
        }

        if (body.tokenInfo && body.tokenInfo.expires_in && body.tokenInfo.access_token) {
          const expiresAt = new Date(new Date().getTime() + (1000 * body.tokenInfo.expires_in));

          dispatch(login(body.tokenInfo.access_token, expiresAt, pageAuthState.isTokenRefresh));
          setAuthState(pageAuthState.isTokenRefresh 
          ? STATE_LOGGED_IN_REFRESH_PROFILE_NO_REDIRECT 
          : STATE_LOGGED_IN_REFRESH_PROFILE);
          redirectAfterLogin(); 
          return;
        }

        setAuthState(STATE_MEDIATOR_FAILURE);

      } catch (e) {
        setAuthState(STATE_MEDIATOR_FAILURE);
      }
      
      popupTimerRef.current = null;
      popupRef.current = null;
      if (popupRedoIfClosed.current) {
        popupRedoIfClosed.current = false;
        setAuthState(STATE_LOGGED_OUT);
      }
        
      }, 1000);
    }

    const monitorPopup = () => {
      console.log("monitorPopup -> start monitor");
      const popup = popupRef.current;
      if (!popup) {
        console.warn("monitorPopup -> no access to popup - aborting");
        return;
      }

      popupTimerRef.current = setInterval(() => {

        if (!popupRedoIfClosed.current) {
          clearInterval(popupTimerRef.current);
          popupTimerRef.current = null;
          return;
        }

        if (popup.closed) {
          console.log("monitorPopup -> popup is closed");
          clearInterval(popupTimerRef.current);
          popupTimerRef.current = null;
          popupRef.current = null;
          if (popupRedoIfClosed.current) {
            popupRedoIfClosed.current = false;
            setAuthState(STATE_POPUP_FAILED);
          }
        }
      }, 1000);
    }

    const handleExpired = async () => {
      sessionStorage.setItem(constants.KEY_REDIRECT_TO_AFTER_LOGIN, `${location.pathname}`);
      clearCodeVerifier();
      clearCodeChallenge();
      clearCodeResponse();

      dispatch(refreshToken);
      
      patchPageAuthState({
        state: STATE_REFRESH_TOKEN,
        isTokenRefresh: true
      })
      //navigate("/auth");
    }

    const handleLogout = () => {
      clearCodeVerifier();
      clearCodeChallenge();
      clearCodeResponse();
      dispatch(logout({tokenRefreshFlag: false}));
      setAuthState(STATE_LOGGING_OUT_2);
    }

    const handleVerifyToken = () => {
      dispatch(login(auth.token, auth.expiresAt, true));

      if (isAuthenticating) {
        redirectAfterLogin();
        setAuthState(STATE_LOGGED_IN_REDIRECTING);
        return;
      }
      setAuthState(STATE_LOGGED_IN_REFRESH_PROFILE);
    }

    const handleInitializeMediator = async () => {
      try {
        const response = await fetch(constants.LOGIN_MEDIATOR_INITIATE_URL);
        if (response.status !== 200) {
          const body = response.json();
          if (body.errorCode !== 1) {
            setAuthState(STATE_MEDIATOR_FAILURE)
            return;
          }
        }

        monitorMediator();
        setAuthState(STATE_MEDIATOR_POLLING);
      } catch (e) {
        console.error(e);
        setAuthState(STATE_MEDIATOR_FAILURE)
      }
    }

    const handleGenesysClient = async () => {
      const client = platformClient.ApiClient.instance;
      client.setEnvironment(constants.LOGIN_GENESYS_SDK_ENVIRONMENT);
      client.setPersistSettings(true);
      const response = await client.loginPKCEGrant(constants.CLIENT_ID, constants.REDIRECT_URI, {state: "state"});
      console.log(response);
      if (pageAuthState.isTokenRefresh) {
        dispatch(login(client.authData.accessToken, client.authData.tokenExpiryTime, true));
        setAuthState(STATE_LOGGED_IN_REDIRECTING)
      } else {
        setAuthState(STATE_LOGGED_IN_REFRESH_PROFILE);
        dispatch(login(client.authData.accessToken, client.authData.tokenExpiryTime, false));
        redirectAfterLogin();
      }
    }

    const handleRefreshProfile = async () => {
      dispatch(getMyProfile(token));
    }

    handleState();

  }, [pageAuthState, authState, setAuthState, setPageAuthState, patchPageAuthState, isAuthenticating, isLogginOut, isCodeResponse, isErrorResponse, codeChallenge, token, expiresAt, auth, dispatch, redirectAfterLogin, handleAuthError, profile]);

  //end of long useEffect

  //effect that gets the user profile
  /*useEffect(() => {
    const [signal, controller] = getAbortSignal();

    console.count("Authentication -> useEffect profile");
    if (!auth.token) return;
    //if (isExpired) return;
    if (authState !== STATE_LOGGED_IN) return;
    
    console.count("Authentication -> useEffect profile: getMyProfile");

    dispatch(getMyProfile(auth.token, signal));

    //cleanup functions appear to be a necessity nowadays for future react versions regarding improved performance called "reusable state"
    //https://reactjs.org/docs/strict-mode.html#ensuring-reusable-state
    return () => {
      console.count("Authentication -> cancel getMyProfile");
      controller.abort();
    }
  }, [dispatch, auth.token, /*isExpired,/ authState, pageAuthState]);*/

  const handleOnRetry = useCallback(() => {
    setAuthState(STATE_LOGGED_OUT);
    navigate("/auth")
  }, [setAuthState]);

  if (isIframe) {
    if (authState === STATE_IFRAME_SUCCESS) {
      return (<div style={{width: "100%", height: "100%", backGroundColor:"green"}}>2</div>)
    }

    if (authState === STATE_IFRAME_ERROR) {
      return (<div style={{width: "100%", height: "100%", backGroundColor:"red"}}>0</div>)
    }

    return (<div style={{width: "100%", height: "100%", backGroundColor:"yellow"}}>1</div>)
  }

  if (pageAuthState.isTokenRefresh) {
    return (props.children);
  }

  if (authState === STATE_LOGGED_IN) {
    return (props.children);
  }

  if (authState === STATE_LOGGED_IN_REDIRECTING) {
    return (props.children);
  }

  if (authState === STATE_LOGGED_OUT) {
    return (props.children);
  }

  if (authState === LOGGING_IN_CODE_REQUEST_IFRAME) {

    const handleLoginFromPopup = () => {
      window.open(constants.LOGIN_BASE_URL, AUTH_POPUP_WINDOW_NAME, "'width=600,height=400,resizable=yes,scrollbars=yes'");
    }

    const loginUrl = generatePKCERedirectCodeUrl(codeChallenge);

    return (<AuthenticatingGenesysDesktop authState={authState} loginUrl={loginUrl} onLoginFromPopup={handleLoginFromPopup} />);
  }

  if (authState === STATE_MEDIATOR_INIT) {
    return (<MediatorPolling />)
  }

  if (authState === STATE_MEDIATOR_POLLING) {
    return (<MediatorPolling />)
  }

  if (authState === STATE_MEDIATOR_FAILURE || authState === STATE_POPUP_FAILED) {
    return (<AuthenticationError 
      onRetry={handleOnRetry}
      onCancel={() => doLogout(null, {tokenRefreshFlag: false})}
    />);
  }

  //avoid reloading children will keep component's state and unsaved work. If token has expired, this will automatically change the status and unload protected child components
  if (!!token && authState === STATE_DETERMINING_STATUS) {
    return props.children;
  }

  return (<PopupPolling />)

  /*

  if (authState === STATE_DETERMINING_STATUS) {
    return (<></>);
  } 
  
  if (authState === STATE_EXPIRED) {
    return (<></>);
  } 
  
  if (authState === STATE_LOGGING_OUT) {
    return (<></>);
  } 
  
  if (authState === STATE_LOGGING_IN_VERIFIER) {
    return (<></>);
  } 
  
  if (authState === STATE_LOGGING_IN_CHALLENGE) {
    return (<></>);
  } 
  
  if (authState === STATE_LOGGING_IN_AWAITING_CHALLENGE) {
    return (<></>);
  } 

  if (authState === STATE_INVALID_TOKEN) {
    return (<></>);
  } 
  
  if (authState === STATE_LOGGING_IN_TOKEN_REQUEST) {
    return (<></>);
  }
  
  return (<></>);*/
}

export default AuthenticationPKCE;


const generateCodeVerifier = (length = 128) => {
  const charset =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';

  const randomValues = new Uint8Array(length);
  crypto.getRandomValues(randomValues);

  return Array.from(randomValues, x => charset[x % charset.length]).join('');
}

const calculateCodeChallenge = async (verifier) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);

  const digest = await crypto.subtle.digest('SHA-256', data);

  return base64UrlEncode(digest);
}

const base64UrlEncode = (buffer) => {
  return btoa(
    String.fromCharCode(...new Uint8Array(buffer))
  )
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

//sessionstorage does not work with popups

const storeCodeVerifier = (verifier) => {
  if (isPopup) {
     return window.opener.localStorage.setItem(constants.STORAGE_PKCE_VERIFIER, verifier);
  }
  window.localStorage.setItem(constants.STORAGE_PKCE_VERIFIER, verifier);
}

const storeCodeChallenge = (challenge) => {
  if (isPopup) {
     return window.opener.localStorage.setItem(constants.STORAGE_PKCE_CHALLENGE, challenge);
  }
  window.localStorage.setItem(constants.STORAGE_PKCE_CHALLENGE, challenge);
}

/* Coderesponse is immediately sent back from the popup, storage not needed
const storeCodeResponse = (codeResponse) => {
  if (isPopup) {
     return window.opener.localStorage.setItem(constants.STORAGE_PKCE_CODE_RESPONSE, codeResponse);
  }
  window.localStorage.setItem(constants.STORAGE_PKCE_CODE_RESPONSE, codeResponse);
}*/

const getStoredCodeVerifier = () => {
  if (isPopup) {
     return window.opener.localStorage.getItem(constants.STORAGE_PKCE_VERIFIER);
  }
  return window.localStorage.getItem(constants.STORAGE_PKCE_VERIFIER);
}

const getStoredCodeChallenge = () => {
  try {
    if (isPopup) {
      return window.opener.localStorage.getItem(constants.STORAGE_PKCE_CHALLENGE);
    }
    return window.localStorage.getItem(constants.STORAGE_PKCE_CHALLENGE);
  } catch (e) {
    console.error(e.message);
  }
}

/*
const getStoredCodeResponse = () => {
  if (isPopup) {
     return window.opener.localStorage.getItem(constants.STORAGE_PKCE_CODE_RESPONSE);
  }
  return window.localStorage.getItem(constants.STORAGE_PKCE_CODE_RESPONSE);
}*/

const clearCodeVerifier = () => {
  if (isPopup) {
     return window.opener.localStorage.removeItem(constants.STORAGE_PKCE_VERIFIER);
  }
  window.localStorage.removeItem(constants.STORAGE_PKCE_VERIFIER);
}

const clearCodeChallenge = () => {
  if (isPopup) {
     return window.opener.localStorage.removeItem(constants.STORAGE_PKCE_CHALLENGE);
  }
  window.localStorage.removeItem(constants.STORAGE_PKCE_CHALLENGE);
}

const clearCodeResponse = () => {
  if (isPopup) {
     return window.opener.localStorage.removeItem(constants.STORAGE_PKCE_CODE_RESPONSE);
  }
  window.localStorage.removeItem(constants.STORAGE_PKCE_CODE_RESPONSE);
}


const generatePKCERedirectCodeUrl = (challenge) => {
  let redirectUri = constants.REDIRECT_URI;

  if (useGenesysDesktopIframe) {
    redirectUri = constants.REDIRECT_URI_IFRAME;
  } else  if (popupEnabled) {
    redirectUri = constants.REDIRECT_URI_POPUP;
  }

  const params = new URLSearchParams({
    client_id: constants.CLIENT_ID,
    code_challenge: challenge,
    code_verifier: getStoredCodeVerifier(),
    redirect_uri: redirectUri,
    code_challenge_method: constants.PKCE_CHALLENGE_MODE,
    response_type: "code"
  });

  return `${constants.LOGIN_BASE_URL}/oauth/authorize?${params.toString()}`;
}